import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { getCompanyRoles } from '@/lib/utils/permissions';
import { useLensesOptional } from '@/contexts/LensesContext';
import { templatesStore, updateTemplate } from '@/lib/stores/templatesStore';
import { ChevronDown, ChevronRight, Eye, Save, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { useToast } from '@/hooks/use-toast';
import { BASIC_INFO_SECTION_ID, ensureBasicInfoSection } from '@/lib/utils/blueprintConstants';
import { 
  RoleVisibilitySelector, 
  getVisibilityState, 
  updateRoleArraysForState,
  type VisibilityState 
} from '@/components/ui/RoleVisibilitySelector';

export default function StructureSidebarLenses() {
  const { itemId } = useParams<{ itemId: string }>();
  const { selectedGame } = useSimplifiedUserData();
  const lensesContext = useLensesOptional();
  const { toast } = useToast();
  
  const [template, setTemplate] = useState<any>(null);
  const [roles, setRoles] = useState<any[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [originalTemplate, setOriginalTemplate] = useState<any>(null);

  // Debug: Log lensesContext state
  useEffect(() => {
    console.log('[StructureSidebarLenses] LensesContext state:', {
      hasContext: !!lensesContext,
      previewMode: lensesContext?.previewMode,
      selectedRole: lensesContext?.selectedRole,
    });
  }, [lensesContext, lensesContext?.previewMode, lensesContext?.selectedRole]);

  // Get template data on mount and when sections change in context
  useEffect(() => {
    if (!itemId) return;

    // Initial load
    const templates = templatesStore.getData();
    const foundTemplate = templates.find(t => t.id === itemId);
    setTemplate(foundTemplate);
    // Store original template for comparison
    if (foundTemplate && !originalTemplate) {
      setOriginalTemplate(JSON.parse(JSON.stringify(foundTemplate)));
    }

    // NOTE: We don't subscribe to store changes anymore because:
    // 1. Changes now go through LensesContext which updates blueprintState
    // 2. The sidebar reads from lensesContext.blueprintSections
    // 3. This prevents feedback loops and state conflicts
  }, [itemId]);

  // Reset unsaved changes when template changes (e.g., when navigating to a different template)
  useEffect(() => {
    if (template) {
      setOriginalTemplate(JSON.parse(JSON.stringify(template)));
      setHasUnsavedChanges(false);
    }
  }, [itemId]);

  // Get company roles and initialize basic info section if needed
  useEffect(() => {
    if (selectedGame?.company_id) {
      const companyRoles = getCompanyRoles(selectedGame.company_id);
      setRoles(companyRoles);
      
      // Set default to Owner role if not already set
      if (!selectedRoleId && companyRoles.length > 0) {
        const ownerRole = companyRoles.find(r => r.role_name === 'Owner');
        if (ownerRole) {
          setSelectedRoleId(ownerRole.id);
          if (lensesContext) {
            lensesContext.setSelectedRole(ownerRole.id);
          }
        }
      }
      
      // Initialize basic info section if it doesn't exist
      if (template && companyRoles.length > 0) {
        const ownerRole = companyRoles.find(r => r.role_name === 'Owner');
        if (ownerRole) {
          const allRoleIds = companyRoles.map(r => r.id);
          const updatedTemplate = ensureBasicInfoSection(template, ownerRole.id, allRoleIds);
          
          // Only update if changed
          if (updatedTemplate !== template) {
            const templates = templatesStore.getData();
            const updatedTemplates = templates.map(t => 
              t.id === template.id ? updatedTemplate : t
            );
            templatesStore.setData(updatedTemplates);
            setTemplate(updatedTemplate);
          }
        }
      }
    }
  }, [selectedGame?.company_id, selectedRoleId, lensesContext, template?.id]);

  // Update context when role changes
  const handleRoleChange = (roleId: string) => {
    setSelectedRoleId(roleId);
    if (lensesContext) {
      lensesContext.setSelectedRole(roleId);
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };


  // Toggle section visibility for selected role
  const handleToggleSectionVisibility = (sectionId: string, newState: VisibilityState) => {
    if (!selectedRoleId) return;

    // Use context function if available - this keeps left and right in sync
    if (lensesContext?.setSectionVisibilityState) {
      lensesContext.setSectionVisibilityState(sectionId, selectedRoleId, newState);
      setHasUnsavedChanges(true);
      return;
    }

    // Fallback: Update template store directly if context not available
    if (!template) return;

    const templates = templatesStore.getData();
    const updatedTemplates = templates.map(t => {
      if (t.id === template.id) {
        const sections = (typeof t.data?.sections === 'object' && t.data.sections !== null) ? t.data.sections as Record<string, any> : {};
        const updatedSections: Record<string, any> = { ...sections };
        
        // Get or create the section (basic info section might not exist yet)
        if (!updatedSections[sectionId]) {
          updatedSections[sectionId] = {
            _sectionTitle: sectionId === BASIC_INFO_SECTION_ID ? 'Basic Information' : sectionId,
            hiddenForRoles: [],
            readOnlyForRoles: [],
          };
        }
        
        const section = updatedSections[sectionId];

        // Update role arrays based on new state
        const { hiddenForRoles, readOnlyForRoles } = updateRoleArraysForState(
          selectedRoleId,
          newState,
          section.hiddenForRoles,
          section.readOnlyForRoles
        );
        
        // Create NEW section object (immutable update) instead of mutating
        updatedSections[sectionId] = {
          ...section,
          hiddenForRoles,
          readOnlyForRoles,
        };

        // Cascade to all fields in section (skip for basic info section)
        if (sectionId !== BASIC_INFO_SECTION_ID) {
          Object.keys(updatedSections[sectionId]).forEach(key => {
            if (!key.startsWith('_') && typeof updatedSections[sectionId][key] === 'object' && updatedSections[sectionId][key].type) {
              const field = updatedSections[sectionId][key];
              const fieldState = updateRoleArraysForState(
                selectedRoleId,
                newState,
                field.hiddenForRoles,
                field.readOnlyForRoles
              );
              // Create NEW field object (immutable update) instead of mutating
              updatedSections[sectionId][key] = {
                ...field,
                hiddenForRoles: fieldState.hiddenForRoles,
                readOnlyForRoles: fieldState.readOnlyForRoles,
              };
            }
          });
        }

        return {
          ...t,
          data: {
            ...t.data,
            sections: updatedSections,
          },
        };
      }
      return t;
    });

    templatesStore.setData(updatedTemplates);
    const newTemplate = updatedTemplates.find(t => t.id === template.id);
    setTemplate(newTemplate);
    setHasUnsavedChanges(true);
  };

  // Toggle field visibility for selected role
  const handleToggleFieldVisibility = (sectionId: string, fieldKey: string, newState: VisibilityState) => {
    if (!selectedRoleId) return;

    // Use context function if available - this keeps left and right in sync
    if (lensesContext?.setFieldVisibilityState) {
      lensesContext.setFieldVisibilityState(sectionId, fieldKey, selectedRoleId, newState);
      setHasUnsavedChanges(true);
      return;
    }

    // Fallback: Update template store directly if context not available
    if (!template) return;

    const templates = templatesStore.getData();
    const updatedTemplates = templates.map(t => {
      if (t.id === template.id) {
        const sections = (typeof t.data?.sections === 'object' && t.data.sections !== null) ? t.data.sections as Record<string, any> : {};
        const updatedSections: Record<string, any> = { ...(sections as Record<string, any>) };
        
        const section = updatedSections[sectionId];
        if (!section || !section[fieldKey]) return t;

        // CRITICAL: Create a deep copy of the section to avoid mutating the store
        updatedSections[sectionId] = { ...section };
        
        const field = section[fieldKey];
        
        // Update field role arrays based on new state
        const { hiddenForRoles, readOnlyForRoles } = updateRoleArraysForState(
          selectedRoleId,
          newState,
          field.hiddenForRoles,
          field.readOnlyForRoles
        );
        
        // Create NEW field object (immutable update) instead of mutating
        updatedSections[sectionId][fieldKey] = {
          ...field,
          hiddenForRoles,
          readOnlyForRoles,
        };

        // Check if all fields are now visible+editable
        const allFieldsVisibleEditable = Object.keys(updatedSections[sectionId]).every(key => {
          if (key.startsWith('_') || !updatedSections[sectionId][key].type) return true;
          const fld = updatedSections[sectionId][key];
          const state = getVisibilityState(selectedRoleId, fld.hiddenForRoles, fld.readOnlyForRoles);
          return state === 'visible-editable';
        });

        // If all fields are visible+editable, make section visible+editable too
        if (allFieldsVisibleEditable) {
          const sectionState = updateRoleArraysForState(
            selectedRoleId,
            'visible-editable',
            updatedSections[sectionId].hiddenForRoles,
            updatedSections[sectionId].readOnlyForRoles
          );
          // Create NEW section object (immutable update) instead of mutating
          updatedSections[sectionId] = {
            ...updatedSections[sectionId],
            hiddenForRoles: sectionState.hiddenForRoles,
            readOnlyForRoles: sectionState.readOnlyForRoles,
          };
        }

        return {
          ...t,
          data: {
            ...t.data,
            sections: updatedSections,
          },
        };
      }
      return t;
    });

    templatesStore.setData(updatedTemplates);
    const newTemplate = updatedTemplates.find(t => t.id === template.id);
    setTemplate(newTemplate);
    setHasUnsavedChanges(true);
  };

  // Handle saving role visibility changes
  const handleSaveVisibility = async () => {
    if (!template || !itemId) return;

    setIsSaving(true);
    try {
      // Get the latest blueprint sections from context (if available) or use template data
      let dataToSave = template.data;
      
      if (lensesContext?.blueprintSections && lensesContext.blueprintSections.length > 0) {
        // Convert context sections (FormSection[] format) back to template data format
        const sectionsObject: any = {};
        
        lensesContext.blueprintSections.forEach(section => {
          sectionsObject[section.id] = {
            _sectionTitle: section.title,
            _fieldOrder: section.fields.map(f => f.id),
            hiddenForRoles: section.hiddenForRoles || [],
            ...Object.fromEntries(
              section.fields.map(field => [
                field.id,
                {
                  ...field,
                  hiddenForRoles: field.hiddenForRoles || [],
                }
              ])
            )
          };
        });

        dataToSave = {
          ...template.data,
          sections: sectionsObject,
        };
      }

      // IMPORTANT: Send current version to prevent version increment
      // Role visibility changes should not create a new template version
      // The backend recognizes this as a metadata-only change (similar to documentation updates)
      const updatedBlueprint = await updateTemplate(
        {
          id: template.id,
          game_id: template.game_id,
          name: template.name,
          tags: template.tags || [],
          type: template.type,
          description: template.description || '',
          data: dataToSave,
          settings: template.settings || {},
          folder: template.folder,
          version: template.version, // Keep same version (no version increment)
        },
        toast
      );

      // Get the latest template from the store (which was updated by updateTemplate)
      const templates = templatesStore.getData();
      const latestTemplate = templates.find(t => t.id === itemId);
      
      // Update the original template to the new saved state
      if (latestTemplate) {
        setOriginalTemplate(JSON.parse(JSON.stringify(latestTemplate)));
        setTemplate(latestTemplate);
      }
      
      setHasUnsavedChanges(false);

      toast({
        title: 'Success',
        description: 'Role visibility settings saved successfully',
      });
    } catch (error) {
      console.error('Error saving visibility settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save visibility settings',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle discarding role visibility changes
  const handleDiscardChanges = () => {
    if (!originalTemplate) return;

    // Use the context's reset function to restore initial data
    if (lensesContext?.resetToInitialData) {
      lensesContext.resetToInitialData();
      setHasUnsavedChanges(false);

      toast({
        title: 'Changes Discarded',
        description: 'Role visibility changes have been discarded',
      });
    } else {
      // Fallback: restore the original template to store (shouldn't happen)
      const templates = templatesStore.getData();
      const updatedTemplates = templates.map(t => 
        t.id === originalTemplate.id ? JSON.parse(JSON.stringify(originalTemplate)) : t
      );
      templatesStore.setData(updatedTemplates);
      setTemplate(JSON.parse(JSON.stringify(originalTemplate)));
      setHasUnsavedChanges(false);

      toast({
        title: 'Changes Discarded',
        description: 'Role visibility changes have been discarded',
      });
    }
  };

  // Use blueprint sections from context (synced with left side)
  // Fallback to template data if context not available
  const blueprintSections = lensesContext?.blueprintSections;
  
  let sections: any = {};
  let sectionIds: string[] = [];
  
  if (blueprintSections && blueprintSections.length > 0) {
    // Convert FormSection[] format to template sections format for rendering
    sections = {};
    blueprintSections.forEach(section => {
      sections[section.id] = {
        _sectionTitle: section.title,
        _fieldOrder: section.fieldOrder,
        hiddenForRoles: section.hiddenForRoles,
        readOnlyForRoles: section.readOnlyForRoles,
      };
      // Add fields to the section
      section.fields.forEach(field => {
        sections[section.id][field.id] = {
          type: field.type,
          label: field.label,
          hiddenForRoles: field.hiddenForRoles,
          readOnlyForRoles: field.readOnlyForRoles,
        };
      });
    });
    sectionIds = blueprintSections.map(s => s.id);
  } else if (template?.data?.sections) {
    // Fallback to template data if context not available
    sections = template.data.sections;
    sectionIds = template.data.elementOrder || Object.keys(sections);
  } else {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        No template loaded
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="p-4 border-b border-border bg-card">
        <div className="flex items-center gap-2 mb-3">
          <Eye className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">Role Visibility</h3>
        </div>
        
        {/* Role Selector */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Select Role</label>
          <Select value={selectedRoleId || ''} onValueChange={handleRoleChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {roles.map(role => (
                <SelectItem key={role.id} value={role.id}>
                  <div className="flex items-center gap-2">
                    {role.data?.color && (
                      <div 
                        className="w-2.5 h-2.5 rounded-full border border-border flex-shrink-0" 
                        style={{ backgroundColor: role.data.color }}
                      />
                    )}
                    <span>{role.role_name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Preview Mode Toggle */}
        <div className="mt-3 flex items-center justify-between p-2 bg-background rounded">
          <span className="text-xs font-medium text-foreground">Preview Mode</span>
          {!lensesContext && (
            <div className="text-xs text-destructive">Context not available</div>
          )}
          <Button
            variant={lensesContext?.previewMode ? "default" : "outline"}
            size="sm"
            onClick={() => {
              console.log('[StructureSidebarLenses] Preview button clicked');
              console.log('[StructureSidebarLenses] Context available:', !!lensesContext);
              console.log('[StructureSidebarLenses] Current preview mode:', lensesContext?.previewMode);
              if (lensesContext) {
                const newMode = !lensesContext.previewMode;
                lensesContext.setPreviewMode(newMode);
                console.log('[StructureSidebarLenses] Preview mode toggled to:', newMode);
              } else {
                console.error('[StructureSidebarLenses] No context available!');
              }
            }}
            className="h-7 text-xs gap-1.5"
            disabled={!lensesContext}
          >
            <Eye className="h-3 w-3" />
            {lensesContext?.previewMode ? 'Previewing' : 'Preview Off'}
          </Button>
        </div>

        <div 
          className="mt-2 p-2 rounded text-xs flex items-center gap-1.5 flex-wrap"
          style={{
            backgroundColor: roles.find(r => r.id === selectedRoleId)?.data?.color 
              ? `${roles.find(r => r.id === selectedRoleId)?.data?.color}15` 
              : '#dbeafe',
            borderLeft: `3px solid ${roles.find(r => r.id === selectedRoleId)?.data?.color || '#3b82f6'}`,
            color: roles.find(r => r.id === selectedRoleId)?.data?.color || '#1e40af'
          }}
        >
          {lensesContext?.previewMode ? (
            <>
              <span><strong>Previewing</strong> what</span>
              {roles.find(r => r.id === selectedRoleId)?.data?.color && (
                <div 
                  className="w-2.5 h-2.5 rounded-full border flex-shrink-0" 
                  style={{ 
                    backgroundColor: roles.find(r => r.id === selectedRoleId)?.data?.color,
                    borderColor: roles.find(r => r.id === selectedRoleId)?.data?.color
                  }}
                />
              )}
              <span className="font-bold">
                {roles.find(r => r.id === selectedRoleId)?.role_name || 'selected role'}
              </span>
              <span>can see</span>
            </>
          ) : (
            <>
              <span>Toggle visibility for</span>
              {roles.find(r => r.id === selectedRoleId)?.data?.color && (
                <div 
                  className="w-2.5 h-2.5 rounded-full border flex-shrink-0" 
                  style={{ 
                    backgroundColor: roles.find(r => r.id === selectedRoleId)?.data?.color,
                    borderColor: roles.find(r => r.id === selectedRoleId)?.data?.color
                  }}
                />
              )}
              <span className="font-bold">
                {roles.find(r => r.id === selectedRoleId)?.role_name || 'selected role'}
              </span>
            </>
          )}
        </div>

        {/* Save/Discard buttons when there are unsaved changes */}
        {hasUnsavedChanges && (
          <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded space-y-2">
            <div className="text-xs text-amber-800 dark:text-amber-200 font-medium mb-2">
              You have unsaved visibility changes
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSaveVisibility}
                disabled={isSaving}
                size="sm"
                className="flex-1 gap-1.5"
              >
                <Save className="h-3 w-3" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                onClick={handleDiscardChanges}
                disabled={isSaving}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                Discard
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Sections & Fields List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {/* Basic Information Section (special) */}
        {selectedRoleId && (
          <div className="mb-4 pb-4 border-b border-border">
            <div 
              className="flex items-center justify-between p-2 hover:bg-muted rounded"
            >
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Basic Information</span>
              </div>
              <RoleVisibilitySelector
                state={getVisibilityState(
                  selectedRoleId,
                  template?.data?.sections?.[BASIC_INFO_SECTION_ID]?.hiddenForRoles,
                  template?.data?.sections?.[BASIC_INFO_SECTION_ID]?.readOnlyForRoles
                )}
                onStateChange={(newState) => handleToggleSectionVisibility(BASIC_INFO_SECTION_ID, newState)}
                disabled={!selectedRoleId}
              />
            </div>
            <div className="ml-6 mt-1 text-xs text-muted-foreground">
              Controls access to template name, description, tags, and metadata
            </div>
          </div>
        )}

        {sectionIds.map(sectionId => {
          const section = sections[sectionId];
          if (!section) return null;

          const sectionTitle = section._sectionTitle || sectionId;
          const isExpanded = expandedSections.has(sectionId);
          
          // Get fields from section
          const fields = Object.keys(section)
            .filter(key => !key.startsWith('_') && typeof section[key] === 'object' && section[key].type)
            .map(key => ({ key, ...section[key] }));

          const sectionState = getVisibilityState(selectedRoleId, section.hiddenForRoles, section.readOnlyForRoles);
          
          return (
            <div key={sectionId} className="border border-border rounded bg-card">
              {/* Section Header */}
              <div className="flex items-center gap-2 p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSection(sectionId)}
                  className="p-0 h-5 w-5"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
                
                <span className={`flex-1 text-sm font-medium ${
                  sectionState === 'hidden' ? 'text-muted-foreground line-through' :
                  sectionState === 'visible-readonly' ? 'text-amber-600 dark:text-amber-400' : ''
                }`}>
                  {sectionTitle}
                </span>
                
                <RoleVisibilitySelector
                  state={sectionState}
                  onStateChange={(newState) => handleToggleSectionVisibility(sectionId, newState)}
                  disabled={!selectedRoleId}
                />
              </div>

              {/* Fields List */}
              {isExpanded && fields.length > 0 && (
                <div className="border-t border-border bg-background">
                  {fields.map(field => {
                    const fieldState = getVisibilityState(selectedRoleId, field.hiddenForRoles, field.readOnlyForRoles);
                    return (
                      <div key={field.key} className="flex items-center gap-2 p-2 pl-8 border-b border-border/50 last:border-b-0">
                        <span className={`flex-1 text-xs ${
                          fieldState === 'hidden' ? 'text-muted-foreground line-through' :
                          fieldState === 'visible-readonly' ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'
                        }`}>
                          {field.label || field.key}
                        </span>
                        <RoleVisibilitySelector
                          state={fieldState}
                          onStateChange={(newState) => handleToggleFieldVisibility(sectionId, field.key, newState)}
                          disabled={!selectedRoleId}
                          size="sm"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

